import { useState, useEffect } from 'react';
import {
  PackageCheck,
  Search,
  Loader2,
  Package,
  Truck,
  CheckCircle2,
  Clock,
  XCircle,
  MoreHorizontal,
  Eye,
  Play,
  Send,
  MapPin,
  User,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { subscribeToFulfillments, unsubscribe } from '@/lib/realtime';

interface Fulfillment {
  id: string;
  organization_id: string;
  order_id: string;
  location_id: string;
  fulfillment_number: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'canceled';
  tracking_number: string | null;
  carrier: string | null;
  shipping_method: string | null;
  shipping_cost: number;
  shipping_address: {
    name?: string;
    line1?: string;
    line2?: string;
    city?: string;
    parish?: string;
    phone?: string;
  } | null;
  picked_at: string | null;
  packed_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  notes: string | null;
  created_at: string;
  order?: {
    id: string;
    order_number: string;
    customer?: { first_name: string | null; last_name: string | null } | null;
  } | null;
  location?: { id: string; name: string } | null;
}

interface FulfillmentItem {
  id: string;
  fulfillment_id: string;
  order_item_id: string;
  product_id: string;
  quantity_ordered: number;
  quantity_fulfilled: number;
  quantity_remaining: number;
  product_name: string;
  product_sku: string | null;
  is_picked: boolean;
  is_packed: boolean;
  product?: { id: string; name: string; image_url: string | null } | null;
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: Package },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  canceled: { label: 'Canceled', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

const CARRIERS = [
  { value: 'local', label: 'Local Delivery' },
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'usps', label: 'USPS' },
  { value: 'dhl', label: 'DHL' },
  { value: 'other', label: 'Other' },
];

export default function Fulfillments() {
  const { organization, user } = useAuth();
  const queryClient = useQueryClient();

  // Real-time subscription for fulfillment updates
  useEffect(() => {
    if (!organization?.id) return;
    const channel = subscribeToFulfillments(organization.id, () => {
      queryClient.invalidateQueries({ queryKey: ['fulfillments'] });
    });
    return () => { unsubscribe(channel); };
  }, [organization?.id, queryClient]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [selectedFulfillment, setSelectedFulfillment] = useState<Fulfillment | null>(null);
  const [selectedItems, setSelectedItems] = useState<FulfillmentItem[]>([]);

  // Ship form state
  const [shipFormData, setShipFormData] = useState({
    carrier: '',
    tracking_number: '',
    shipping_method: '',
    notes: '',
  });

  // Fetch fulfillments
  const { data: fulfillments = [], isLoading } = useQuery({
    queryKey: ['fulfillments', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('fulfillments')
        .select(`
          *,
          order:orders(id, order_number, customer:customers(first_name, last_name)),
          location:locations(id, name)
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Fulfillment[];
    },
    enabled: !!organization?.id,
  });

  // Fetch fulfillment items
  const fetchFulfillmentItems = async (fulfillmentId: string) => {
    const { data, error } = await supabase
      .from('fulfillment_items')
      .select(`
        *,
        product:products(id, name, image_url)
      `)
      .eq('fulfillment_id', fulfillmentId);
    if (error) throw error;
    return data as FulfillmentItem[];
  };

  // Update fulfillment status mutation
  const updateStatus = useMutation({
    mutationFn: async ({ fulfillmentId, status }: { fulfillmentId: string; status: string }) => {
      const { error } = await supabase.rpc('update_fulfillment_status', {
        p_fulfillment_id: fulfillmentId,
        p_status: status,
        p_user_id: user?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fulfillments'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Status updated');
    },
  });

  // Ship fulfillment mutation
  const shipFulfillment = useMutation({
    mutationFn: async (fulfillmentId: string) => {
      const { error } = await supabase
        .from('fulfillments')
        .update({
          status: 'shipped',
          carrier: shipFormData.carrier || null,
          tracking_number: shipFormData.tracking_number || null,
          shipping_method: shipFormData.shipping_method || null,
          notes: shipFormData.notes || null,
          shipped_at: new Date().toISOString(),
          shipped_by: user?.id,
        })
        .eq('id', fulfillmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fulfillments'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Fulfillment shipped');
      setShipDialogOpen(false);
      resetShipForm();
    },
  });

  // Cancel fulfillment mutation
  const cancelFulfillment = useMutation({
    mutationFn: async (fulfillmentId: string) => {
      const { error } = await supabase
        .from('fulfillments')
        .update({ status: 'canceled' })
        .eq('id', fulfillmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fulfillments'] });
      toast.success('Fulfillment canceled');
    },
  });

  // Filter fulfillments
  const filteredFulfillments = fulfillments.filter((f) => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        f.fulfillment_number.toLowerCase().includes(query) ||
        f.order?.order_number?.toLowerCase().includes(query) ||
        f.tracking_number?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Count by status
  const statusCounts = {
    pending: fulfillments.filter((f) => f.status === 'pending').length,
    processing: fulfillments.filter((f) => f.status === 'processing').length,
    shipped: fulfillments.filter((f) => f.status === 'shipped').length,
    delivered: fulfillments.filter((f) => f.status === 'delivered').length,
  };

  const openViewDialog = async (fulfillment: Fulfillment) => {
    setSelectedFulfillment(fulfillment);
    const items = await fetchFulfillmentItems(fulfillment.id);
    setSelectedItems(items);
    setViewDialogOpen(true);
  };

  const openShipDialog = async (fulfillment: Fulfillment) => {
    setSelectedFulfillment(fulfillment);
    const items = await fetchFulfillmentItems(fulfillment.id);
    setSelectedItems(items);
    setShipDialogOpen(true);
  };

  const resetShipForm = () => {
    setShipFormData({
      carrier: '',
      tracking_number: '',
      shipping_method: '',
      notes: '',
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Fulfillments</h2>
          <p className="text-sm text-muted-foreground">
            Track and manage order fulfillments
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <Label className="mb-1.5 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Order #, fulfillment #, or tracking"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[150px]">
          <Label className="mb-1.5 block">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card
          className={cn(
            'cursor-pointer transition-colors',
            statusFilter === 'pending' && 'ring-2 ring-yellow-500'
          )}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-yellow-500/10">
                <Clock className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{statusCounts.pending}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            'cursor-pointer transition-colors',
            statusFilter === 'processing' && 'ring-2 ring-blue-500'
          )}
          onClick={() => setStatusFilter(statusFilter === 'processing' ? 'all' : 'processing')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-blue-500/10">
                <Package className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{statusCounts.processing}</p>
                <p className="text-xs text-muted-foreground">Processing</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            'cursor-pointer transition-colors',
            statusFilter === 'shipped' && 'ring-2 ring-purple-500'
          )}
          onClick={() => setStatusFilter(statusFilter === 'shipped' ? 'all' : 'shipped')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-purple-500/10">
                <Truck className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{statusCounts.shipped}</p>
                <p className="text-xs text-muted-foreground">Shipped</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            'cursor-pointer transition-colors',
            statusFilter === 'delivered' && 'ring-2 ring-green-500'
          )}
          onClick={() => setStatusFilter(statusFilter === 'delivered' ? 'all' : 'delivered')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-green-500/10">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{statusCounts.delivered}</p>
                <p className="text-xs text-muted-foreground">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fulfillments List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading fulfillments...</span>
          </div>
        </div>
      ) : filteredFulfillments.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <PackageCheck className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No fulfillments</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Orders that require fulfillment will appear here
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Fulfillment
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Order
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Customer
                </th>
                <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                  Status
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Tracking
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Date
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredFulfillments.map((fulfillment) => {
                const statusConfig = STATUS_CONFIG[fulfillment.status];
                const StatusIcon = statusConfig.icon;
                return (
                  <tr key={fulfillment.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openViewDialog(fulfillment)}
                        className="font-medium text-sm hover:underline"
                      >
                        {fulfillment.fulfillment_number}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {fulfillment.order?.order_number || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {fulfillment.order?.customer ? (
                        `${fulfillment.order.customer.first_name || ''} ${fulfillment.order.customer.last_name || ''}`
                      ) : (
                        <span className="text-muted-foreground">Guest</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={cn('gap-1', statusConfig.color)}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {fulfillment.tracking_number ? (
                        <span className="font-mono">{fulfillment.tracking_number}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(fulfillment.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewDialog(fulfillment)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {fulfillment.status === 'pending' && (
                            <DropdownMenuItem
                              onClick={() => updateStatus.mutateAsync({
                                fulfillmentId: fulfillment.id,
                                status: 'processing',
                              })}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Start Processing
                            </DropdownMenuItem>
                          )}
                          {fulfillment.status === 'processing' && (
                            <DropdownMenuItem onClick={() => openShipDialog(fulfillment)}>
                              <Send className="h-4 w-4 mr-2" />
                              Ship
                            </DropdownMenuItem>
                          )}
                          {fulfillment.status === 'shipped' && (
                            <DropdownMenuItem
                              onClick={() => updateStatus.mutateAsync({
                                fulfillmentId: fulfillment.id,
                                status: 'delivered',
                              })}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Mark Delivered
                            </DropdownMenuItem>
                          )}
                          {(fulfillment.status === 'pending' || fulfillment.status === 'processing') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  if (confirm('Cancel this fulfillment?')) {
                                    cancelFulfillment.mutateAsync(fulfillment.id);
                                  }
                                }}
                                className="text-red-600"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Cancel
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* View Fulfillment Dialog */}
      <ResponsiveModal open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              Fulfillment {selectedFulfillment?.fulfillment_number}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Order {selectedFulfillment?.order?.order_number} • {selectedFulfillment?.location?.name}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-6">
              {/* Status and Timeline */}
              <div className="flex items-center gap-4 flex-wrap">
                {selectedFulfillment && (
                  <Badge className={cn('gap-1', STATUS_CONFIG[selectedFulfillment.status].color)}>
                    {STATUS_CONFIG[selectedFulfillment.status].label}
                  </Badge>
                )}
                {selectedFulfillment?.carrier && (
                  <span className="text-sm text-muted-foreground">
                    <Truck className="h-4 w-4 inline mr-1" />
                    {selectedFulfillment.carrier}
                  </span>
                )}
                {selectedFulfillment?.tracking_number && (
                  <span className="text-sm font-mono">
                    #{selectedFulfillment.tracking_number}
                  </span>
                )}
              </div>

              {/* Timeline */}
              <div className="grid grid-cols-4 gap-4 p-4 rounded-lg bg-muted/50">
                <div className="text-center">
                  <div className={cn(
                    'w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-2',
                    selectedFulfillment?.created_at ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
                  )}>
                    <Package className="h-4 w-4" />
                  </div>
                  <p className="text-xs font-medium">Created</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFulfillment?.created_at && new Date(selectedFulfillment.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-center">
                  <div className={cn(
                    'w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-2',
                    selectedFulfillment?.picked_at ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
                  )}>
                    <User className="h-4 w-4" />
                  </div>
                  <p className="text-xs font-medium">Picked</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFulfillment?.picked_at ? new Date(selectedFulfillment.picked_at).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div className="text-center">
                  <div className={cn(
                    'w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-2',
                    selectedFulfillment?.shipped_at ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
                  )}>
                    <Truck className="h-4 w-4" />
                  </div>
                  <p className="text-xs font-medium">Shipped</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFulfillment?.shipped_at ? new Date(selectedFulfillment.shipped_at).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div className="text-center">
                  <div className={cn(
                    'w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-2',
                    selectedFulfillment?.delivered_at ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
                  )}>
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <p className="text-xs font-medium">Delivered</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFulfillment?.delivered_at ? new Date(selectedFulfillment.delivered_at).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>

              {/* Shipping Address */}
              {selectedFulfillment?.shipping_address && (
                <div>
                  <Label className="text-muted-foreground flex items-center gap-1 mb-2">
                    <MapPin className="h-4 w-4" />
                    Shipping Address
                  </Label>
                  <div className="p-3 rounded-lg border text-sm">
                    {selectedFulfillment.shipping_address.name && (
                      <p className="font-medium">{selectedFulfillment.shipping_address.name}</p>
                    )}
                    {selectedFulfillment.shipping_address.line1 && (
                      <p>{selectedFulfillment.shipping_address.line1}</p>
                    )}
                    {selectedFulfillment.shipping_address.line2 && (
                      <p>{selectedFulfillment.shipping_address.line2}</p>
                    )}
                    <p>
                      {[
                        selectedFulfillment.shipping_address.city,
                        selectedFulfillment.shipping_address.parish,
                      ].filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>
              )}

              {/* Items */}
              <div>
                <Label className="text-muted-foreground mb-2 block">Items</Label>
                <div className="rounded-lg border">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left text-sm font-medium text-muted-foreground px-4 py-2">
                          Product
                        </th>
                        <th className="text-right text-sm font-medium text-muted-foreground px-4 py-2">
                          Ordered
                        </th>
                        <th className="text-right text-sm font-medium text-muted-foreground px-4 py-2">
                          Fulfilled
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedItems.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-3">
                              {item.product?.image_url ? (
                                <img
                                  src={item.product.image_url}
                                  alt={item.product_name}
                                  className="w-8 h-8 rounded object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                  <Package className="h-4 w-4 text-muted-foreground" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-sm">{item.product_name}</p>
                                {item.product_sku && (
                                  <p className="text-xs text-muted-foreground">{item.product_sku}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {item.quantity_ordered}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {item.quantity_fulfilled}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              {selectedFulfillment?.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm mt-1">{selectedFulfillment.notes}</p>
                </div>
              )}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Ship Dialog */}
      <ResponsiveModal open={shipDialogOpen} onOpenChange={setShipDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Ship Fulfillment</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Add shipping details for {selectedFulfillment?.fulfillment_number}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Carrier</Label>
                  <Select
                    value={shipFormData.carrier}
                    onValueChange={(value) => setShipFormData({ ...shipFormData, carrier: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select carrier" />
                    </SelectTrigger>
                    <SelectContent>
                      {CARRIERS.map((carrier) => (
                        <SelectItem key={carrier.value} value={carrier.value}>
                          {carrier.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Shipping Method</Label>
                  <Input
                    value={shipFormData.shipping_method}
                    onChange={(e) => setShipFormData({ ...shipFormData, shipping_method: e.target.value })}
                    placeholder="e.g., Standard, Express"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tracking Number</Label>
                <Input
                  value={shipFormData.tracking_number}
                  onChange={(e) => setShipFormData({ ...shipFormData, tracking_number: e.target.value })}
                  placeholder="Enter tracking number"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={shipFormData.notes}
                  onChange={(e) => setShipFormData({ ...shipFormData, notes: e.target.value })}
                  placeholder="Any shipping notes..."
                  rows={2}
                />
              </div>

              {/* Items summary */}
              <div className="rounded-lg border p-3">
                <Label className="text-muted-foreground text-xs uppercase mb-2 block">Items to Ship</Label>
                {selectedItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between py-1.5">
                    <span className="text-sm">{item.product_name}</span>
                    <span className="text-sm font-medium tabular-nums">{item.quantity_ordered}</span>
                  </div>
                ))}
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setShipDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={() => selectedFulfillment && shipFulfillment.mutateAsync(selectedFulfillment.id)}
              disabled={shipFulfillment.isPending}
            >
              {shipFulfillment.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Truck className="h-4 w-4 mr-2" />
              Ship
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
